import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { ActivityLogModalComponent } from '../../components/activity-log-modal/activity-log-modal.component';
import { AddUserModalComponent, UserAddedEvent } from '../../components/add-user-modal/add-user-modal.component';
import { ConfirmModalComponent } from '../../components/confirm-modal/confirm-modal.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { AccessType, AppUser, BuildingContact, LogEntry } from '../../../shared/models/user.model';
import { Icons } from '../../../shared/helpers/icons';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { LockdownUserService } from '../../../shared/services/lockdown-user.service';

type SortableColumn = 'name' | 'email' | 'department' | 'accessType';

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    NgxPaginationModule,
    ConfirmModalComponent,
    AddUserModalComponent,
    ActivityLogModalComponent,
    SpinnerComponent
  ],
  templateUrl: './user.component.html'
})
export class UserComponent implements OnInit, OnDestroy {
  icons = Icons;
  readonly AccessType = AccessType;
  readonly accessTypeOptions = Object.values(AccessType);

  users:         AppUser[] = [];
  filteredUsers: AppUser[] = [];
  searchQuery      = '';
  filterAccessType: AccessType | '' = '';
  filterClientId   = '';
  clientOptions:   { id: string; name: string }[] = [];
  page = 1;
  readonly itemsPerPage = 10;

  sortColumn: SortableColumn | null = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  showAddModal = false;
  showLogModal = false;
  loadingModal = false;
  loadingLogs  = false;
  loading      = false;
  saving       = false;

  logs: LogEntry[] = [];
  tooltipX       = 0;
  tooltipY       = 0;
  exportTooltipX = 0;
  exportTooltipY = 0;
  private readonly destroy$     = new Subject<void>();
  private readonly cancelFetch$ = new Subject<void>();
  private readonly cancelAdd$   = new Subject<void>();

  confirmModal: { visible: boolean; action: 'single' | 'all'; user?: AppUser } = {
    visible: false,
    action: 'all'
  };

  buildingContacts: BuildingContact[] = [];
  contacts: BuildingContact[] = [];

  constructor(
    protected userDataService: UserDataService,
    private lockdownUserService: LockdownUserService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const buildingId = this.userDataService.buildingId();
    if (!buildingId) return;

    this.loading = true;
    this.lockdownUserService.getUsers(buildingId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: users => {
          this.users   = users;
          this.loading = false;
          this.refreshAddedIds();
          this.filterUsers();
        },
        error: (err: Error) => {
          this.loading = false;
          this.toastr.error(err.message, 'Failed to load users');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelFetch$.next();
    this.cancelFetch$.complete();
    this.cancelAdd$.next();
    this.cancelAdd$.complete();
  }

  onModalClosed(): void {
    this.cancelFetch$.next();
    this.cancelAdd$.next();
    this.showAddModal = false;
  }

  openAddModal(): void {
    this.showAddModal = true;
    const buildingId = this.userDataService.buildingId();
    if (buildingId) this.fetchUsersAndContacts(buildingId);
  }

  private fetchUsersAndContacts(buildingId: string): void {
    this.cancelFetch$.next();
    this.loadingModal = true;
    this.lockdownUserService.getUsersAndContacts(buildingId)
      .pipe(takeUntil(this.cancelFetch$))
      .subscribe({
        next: res => {
          this.buildingContacts = res.users;
          this.contacts         = res.contacts;
          this.loadingModal     = false;
        },
        error: (err: Error) => {
          this.toastr.error(err.message, 'Failed to load users');
          this.loadingModal = false;
        }
      });
  }

  addedIds: string[] = this.users.map((u) => u.id);

  private refreshAddedIds(): void {
    this.addedIds = this.users.filter((u) => this.isCurrentBuilding(u)).map((u) => u.id);
  }

  sortBy(column: SortableColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn   = column;
      this.sortDirection = 'asc';
    }
    this.filterUsers();
  }

  isInherited(user: AppUser): boolean {
    return !!user.inherited && !this.userDataService.isOrg();
  }

  isCurrentBuilding(user: AppUser): boolean {
    if (!this.userDataService.isOrg()) return true;
    return user.clientId === Number(this.userDataService.buildingId());
  }

  trackByClientId(_: number, c: { id: string; name: string }): string {
    return c.id;
  }

  onClientFilterChange(): void {
    this.filterUsers();
  }

  filterUsers(): void {
    const q    = this.searchQuery.toLowerCase();
    const type = this.filterAccessType;

    const preFiltered = this.users.filter(u => {
      if (type && u.accessType !== type) return false;
      return (
        (u.name ?? '').toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q) ||
        (u.department ?? '').toLowerCase().includes(q)
      );
    });

    const seen = new Set<number>();
    this.clientOptions = [];
    for (const u of this.users) {
      if (u.clientId !== undefined && !seen.has(u.clientId)) {
        seen.add(u.clientId);
        this.clientOptions.push({
          id:   String(u.clientId),
          name: u.clientName ?? `Building ${u.clientId}`
        });
      }
    }

    if (this.userDataService.isOrg()) {
      const currentId = this.userDataService.buildingId() ?? '';
      if (currentId && !this.clientOptions.some(c => c.id === currentId)) {
        this.clientOptions.unshift({ id: currentId, name: 'Current Building' });
      }
      if (!this.filterClientId) {
        this.filterClientId = this.clientOptions.some(c => c.id === currentId)
          ? currentId
          : (this.clientOptions[0]?.id ?? '');
      }
      if (this.filterClientId && !this.clientOptions.some(c => c.id === this.filterClientId)) {
        this.filterClientId = currentId || (this.clientOptions[0]?.id ?? '');
      }
    }

    this.filteredUsers = this.filterClientId
      ? preFiltered.filter(u => String(u.clientId) === this.filterClientId)
      : preFiltered;

    if (this.sortColumn) {
      const col = this.sortColumn;
      const dir = this.sortDirection === 'asc' ? 1 : -1;
      this.filteredUsers.sort((a, b) => (a[col] ?? '').localeCompare(b[col] ?? '') * dir);
    }
    this.filteredUsers.sort((a, b) => (a.inherited ? 1 : 0) - (b.inherited ? 1 : 0));
    this.page = 1;
  }

  onUserAdded(event: UserAddedEvent): void {
    const buildingId = this.userDataService.buildingId();
    if (!buildingId) {
      this.toastr.error('No building selected');
      return;
    }

    this.saving = true;
    this.lockdownUserService.addUser(buildingId, event.contact.id, event.accessType, event.isContact)
      .pipe(takeUntil(this.cancelAdd$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.reloadUsers();
          this.toastr.success(`${event.contact.name} added successfully`);
        },
        error: (err: Error) => {
          this.saving = false;
          this.toastr.error(err.message, 'Failed to add user');
        }
      });
  }

  onAccessTypeChange(user: AppUser, newType: AccessType): void {
    const buildingId = user.clientId ? String(user.clientId) : this.userDataService.buildingId();
    if (!buildingId) return;

    const previousType  = user.accessType;
    user.accessType     = newType;

    this.saving = true;
    this.lockdownUserService.updateUser(buildingId, user.id, user.isContact ?? false, newType)
      .subscribe({
        next: () => {
          this.saving = false;
          this.toastr.success(`${user.name}'s access updated to "${newType}"`);
          this.reloadUsers();
        },
        error: (err: Error) => {
          this.saving      = false;
          user.accessType  = previousType;
          this.toastr.error(err.message, 'Failed to update access type');
        }
      });
  }

  private reloadUsers(): void {
    const buildingId = this.userDataService.buildingId();
    if (!buildingId) return;
    this.lockdownUserService.getUsers(buildingId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: users => {
          this.users = users;
          this.refreshAddedIds();
          this.filterUsers();
        },
        error: (err: Error) => this.toastr.error(err.message, 'Failed to reload users')
      });
  }

  promptRemoveUser(user: AppUser): void {
    this.confirmModal = { visible: true, action: 'single', user };
  }

  confirmAction(): void {
    if (this.confirmModal.action === 'single' && this.confirmModal.user) {
      const target     = this.confirmModal.user;
      const buildingId = target.clientId ? String(target.clientId) : this.userDataService.buildingId();

      this.confirmModal.visible = false;

      if (!buildingId) {
        this.toastr.error('No building selected');
        return;
      }

      this.saving = true;
      this.lockdownUserService.deleteUser(buildingId, target.id, target.isContact ?? false)
        .subscribe({
          next: () => {
            this.saving = false;
            this.users = this.users.filter((u) => u.id !== target.id);
            this.refreshAddedIds();
            this.filterUsers();
            this.toastr.success(`${target.name} removed successfully`);
          },
          error: (err: Error) => {
            this.saving = false;
            this.toastr.error(err.message, 'Failed to remove user');
          }
        });
    } else {
      this.users = [];
      this.refreshAddedIds();
      this.filterUsers();
      this.confirmModal.visible = false;
    }
  }

  cancelConfirm(): void {
    this.confirmModal.visible = false;
  }

  onRowMouseMove(event: MouseEvent): void {
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY;
  }

  onExportMouseMove(event: MouseEvent): void {
    this.exportTooltipX = event.clientX;
    this.exportTooltipY = event.clientY;
  }

  get exportBuildingName(): string {
    if (this.filterClientId) {
      return this.clientOptions.find(c => c.id === this.filterClientId)?.name ?? '';
    }
    return this.clientOptions[0]?.name ?? this.users[0]?.clientName ?? '';
  }

  openLogModal(): void {
    this.showLogModal = true;
    const buildingId = this.userDataService.buildingId();
    if (!buildingId) return;
    this.loadingLogs = true;
    this.lockdownUserService.getLogs(buildingId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: logs => {
          this.logs        = logs;
          this.loadingLogs = false;
        },
        error: (err: Error) => {
          this.loadingLogs = false;
          this.toastr.error(err.message, 'Failed to load history');
        }
      });
  }

  async exportPdf(): Promise<void> {
    const img = new Image();
    img.src = '/assets/logo.png';
    await new Promise(resolve => { img.onload = resolve; });

    const canvas = document.createElement('canvas');
    canvas.width  = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    const logoBase64   = canvas.toDataURL('image/png');
    const aspectRatio  = img.naturalWidth / img.naturalHeight;
    const logoW        = 8;
    const logoH        = logoW / aspectRatio;

    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text('nSide | Lockdown — User Permissions', 14, 16);
    doc.setFontSize(10);
    doc.setTextColor(150);
    const buildingLabel = this.exportBuildingName;
    if (buildingLabel) doc.text(buildingLabel, 14, 23);
    doc.text(`Generated ${new Date().toLocaleString()}`, 14, buildingLabel ? 29 : 23);
    doc.setTextColor(0);

    const exportUsers = this.userDataService.isOrg() && this.filterClientId
      ? this.users.filter(u => String(u.clientId) === this.filterClientId)
      : this.users;

    autoTable(doc, {
      startY: buildingLabel ? 34 : 28,
      head: [['No.', 'Name', 'Email', 'Department', 'Access Type']],
      body: exportUsers.map((u, i) => [i + 1, u.name, u.email, u.department, u.accessType]),
      headStyles: { fillColor: [230, 90, 30] },
      styles: { fontSize: 9 },
    });

    const pageW      = doc.internal.pageSize.getWidth();
    const pageH      = doc.internal.pageSize.getHeight();
    const margin     = 8;
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.addImage(logoBase64, 'PNG', pageW - logoW - margin, pageH - logoH - margin, logoW, logoH);
    }

    doc.save('lockdown-users.pdf');
  }

  accessTypeBadgeClass(type: AccessType): string {
    switch (type) {
      case AccessType.AdminAppUser: return 'bg-orange-100 text-nside-orange-400';
      case AccessType.MessageOnly:  return 'bg-gray-100 text-gray-600';
      default:                      return 'bg-blue-50 text-blue-600';
    }
  }

  smsConsentLabel(user: AppUser): string {
    if (user.smsConsent === 1) return 'Opted In';
    if (user.smsConsent === 0) return 'Opted Out';
    if (user.smsConsent === 2) return 'Undetermined';
    return 'No Phone Number';
  }

  smsConsentBadgeClass(user: AppUser): string {
    if (user.smsConsent === 1) return 'bg-green-50 text-green-600';
    if (user.smsConsent === 0) return 'bg-red-50 text-red-500';
    if (user.smsConsent === 2) return 'bg-yellow-50 text-yellow-600';
    return 'bg-gray-100 text-gray-500';
  }
}
